/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Subscription } from 'rxjs';
import { Injectable, Injector, NgZone } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { ApplicationError, ChangedEvent, CoreActionKey } from '@typescript-kit/core';
import {
  ActionModel,
  BackdropModel,
  ComponentModel,
  ContainerModel,
  DropdownModel,
  ImageModel,
  LoadIndicatorModel,
  Orientations,
  TextModel,
  ViewComponentKey
} from '@typescript-kit/view';
import { PersistenceService } from '@angular-kit/core';
import { AlertContainerViewService, ViewService } from '@angular-kit/view';
import { AuthenticationService } from '@angular-kit/authentication';

import { ShellViewData, ShellViewFieldKey, ShellViewModel } from './shell.view-model';
import { ShellComponentModel } from '../component/shell/shell-component.model';
import { TimeRecordingInjectionToken } from '../../time-recording/injection-token/time-recording.injection-token';
import { View } from '../model/view.model';
import { ShellPersistenceKey } from '../key/shell-persistence.key';
import { ShellSvgKey } from '../key/shell-svg.key';
import { ShellGlobalTextKey } from '../key/shell-global-text.key';

@Injectable()
export class ShellViewService extends ViewService<ShellViewModel, ShellComponentModel> {
  private readonly documentHiddenKey: string;
  private readonly documentVisibilityChangedKey: string;

  private userChangedSubscription: Subscription;
  private routeChangedSubscription: Subscription;
  private queryParamMapChangedSubscription: Subscription;
  private windowFocusEventListener: () => any;
  private documentVisibilityChangedEventListener: () => any;

  private menuModelStack: ComponentModel[] = [];
  private sidebarModelStack: ComponentModel[] = [];

  private loadingCounter = 0;

  // private appsIconMediaQueryList: MediaQueryList;
  // private appsIconMediaQueryEventHandler: (event: MediaQueryListEvent) => any;

  constructor(
    injector: Injector,
    private readonly title: Title,
    private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authenticationService: AuthenticationService,
    private readonly persistenceService: PersistenceService,
    private readonly alertContainerViewService: AlertContainerViewService
  ) {
    super(injector);
    if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
      this.documentHiddenKey = 'hidden';
      this.documentVisibilityChangedKey = 'visibilitychange';
    } else if (typeof document['msHidden'] !== 'undefined') {
      this.documentHiddenKey = 'msHidden';
      this.documentVisibilityChangedKey = 'msvisibilitychange';
    } else if (typeof document['webkitHidden'] !== 'undefined') {
      this.documentHiddenKey = 'webkitHidden';
      this.documentVisibilityChangedKey = 'webkitvisibilitychange';
    }
  }

  initialize(viewData?: ShellViewData): void {
    const viewModel = viewData && viewData instanceof ShellViewModel ? viewData : new ShellViewModel(viewData);
    viewModel.setValues({
      user: this.authenticationService.currentUser,
    });
    this.queryParamMapChangedSubscription = this.activatedRoute.queryParamMap.subscribe((queryParamMap) => {
      viewModel.isPrinting = queryParamMap.get('print') === 'true';
    });
    // this.appsIconMediaQueryList = window.matchMedia('(max-width: 400px)');
    // this.appsIconMediaQueryEventHandler = (event: MediaQueryListEvent) => this.onAppsIconMediaQueryChanged(event);
    // if (this.appsIconMediaQueryList.addEventListener) {
    //   this.appsIconMediaQueryList.addEventListener('change', this.appsIconMediaQueryEventHandler);
    // } else {
    //   // Required for Safari
    //   this.appsIconMediaQueryList.addListener(this.appsIconMediaQueryEventHandler);
    // }

    this.alertContainerViewService.initialize();
    super.initialize(viewModel);
    this.refreshView();
    this.refreshViewTitle();

    this.userChangedSubscription = this.authenticationService.currentUserChanged
      .subscribe(() => this.onCurrentUserChanged());

    this.routeChangedSubscription = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.onRouteChanged();
      }
    });

    this.windowFocusEventListener = () => {
      setTimeout(() => {
        if (document.activeElement === document.body && ComponentModel.lastFocused) {
          this.ngZone.run(() => ComponentModel.lastFocused.isFocused = true);
        }
      }, 50);
    };
    window.addEventListener('focus', this.windowFocusEventListener, false);

    if (this.documentHiddenKey && this.documentVisibilityChangedKey) {
      this.documentVisibilityChangedEventListener = () => {
        setTimeout(() => {
          if (!document[this.documentHiddenKey] && document.activeElement === document.body && ComponentModel.lastFocused) {
            this.ngZone.run(() => ComponentModel.lastFocused.isFocused = true);
          }
        }, 50);
      };
      document.addEventListener(this.documentVisibilityChangedKey, this.documentVisibilityChangedEventListener, false);
    }

  }

  finalize(): void {
    this.queryParamMapChangedSubscription.unsubscribe();
    this.userChangedSubscription.unsubscribe();
    this.routeChangedSubscription.unsubscribe();
    window.removeEventListener('focus', this.windowFocusEventListener, false);
    if (this.documentVisibilityChangedEventListener) {
      document.removeEventListener(this.documentVisibilityChangedKey, this.documentVisibilityChangedEventListener, false);
    }
    // if (this.appsIconMediaQueryList.removeEventListener) {
    //   this.appsIconMediaQueryList.removeEventListener('change', this.appsIconMediaQueryEventHandler);
    // } else {
    //   // Required for Safari
    //   this.appsIconMediaQueryList.removeListener(this.appsIconMediaQueryEventHandler);
    // }
    // this.appsIconMediaQueryEventHandler = null;
    // this.appsIconMediaQueryList = null;
    this.alertContainerViewService.finalize();
    super.finalize();
  }

  setMenu(menu: ComponentModel): void {
    this.menuModelStack.push(this.componentModel.menuModel);
    this.componentModel.menuModel = menu;
  }

  restoreMenu(): void {
    this.componentModel.menuModel = this.menuModelStack.pop();
  }

  setSidebar(sidebar: ComponentModel): void {
    this.sidebarModelStack.push(this.componentModel.sidebarModel);
    this.componentModel.sidebarModel = sidebar;
  }

  restoreSidebar(): void {
    this.componentModel.sidebarModel = this.sidebarModelStack.pop();
  }

  // private onAppsIconMediaQueryChanged(event: MediaQueryListEvent) {
  //   // TODO
  // }

  private onCurrentUserChanged() {
    this.viewModel.user = this.authenticationService.currentUser;
    this.componentModel.isSidebarVisible = this.viewModel.user ? this.restoreIsSidebarVisible() : false;
    this.refreshSidebarAction(this.componentModel.toggleSidebarAction, this.componentModel.isSidebarVisible);
  }

  protected onRouteChanged() {
    this.refreshView();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      return;
    }
    if (event.changes[ShellViewFieldKey.VIEW] || event.changes[ShellViewFieldKey.APP_TITLE]) {
      this.refreshViewTitle();
    }

    if (event.changes[ShellViewFieldKey.USER]
      || event.changes[ShellViewFieldKey.VIEW_LIST]
      || event.changes[ShellViewFieldKey.VIEW]
    ) {
      if (event.changes[ShellViewFieldKey.USER]) {
        this.refreshUserDropdownModel(this.componentModel.userDropdown);
      }
      if (event.changes[ShellViewFieldKey.VIEW_LIST] || event.changes[ShellViewFieldKey.VIEW]) {
        if (event.changes[ShellViewFieldKey.VIEW_LIST]) {
          this.refreshView();
          this.refreshViewTitle();
        }
        this.refreshIsMenuVisible(this.componentModel);
        this.refreshViewDropdownModel(this.componentModel.viewDropdown);
      }
    }
    if (event.changes[ShellViewFieldKey.IS_LOADING]) {
      this.refreshBackdropModel(this.componentModel.backdropModel);
    }
  }

  public incrementLoadingCounter() {
    this.loadingCounter++;
    this.viewModel.isLoading = true;
    window['__appIsLoading'] = true;
    console.timeStamp('shell-increment-loading-counter');
  }

  public decrementLoadingCounter() {
    this.loadingCounter--;
    if (this.loadingCounter === 0) {
      this.viewModel.isLoading = false;
      window['__appIsLoading'] = false;
      console.timeStamp('shell-decrement-loading-counter');
    } else if (this.loadingCounter < 0) {
      throw new Error('Loading counter must not be less than zero');
    }
  }

  public resetLoadingCounter() {
    this.loadingCounter = 0;
    this.viewModel.isLoading = false;
  }

  private refreshView() {
    const childUrl = this.activatedRoute.snapshot.firstChild && this.activatedRoute.snapshot.firstChild.url;
    const viewId = childUrl && childUrl.length > 0 ? childUrl[0].path : null;
    const viewList = this.viewModel.viewList;
    this.viewModel.view = viewId && viewList ? viewList.find((view) => view.id === viewId) : undefined;
  }

  private refreshViewTitle() {
    if (this.viewModel.view) {
      this.title.setTitle(this.textService.getText(`#(${this.viewModel.view.name}) | #(${ShellGlobalTextKey.VENDOR_SOFTWARE})`));
    } else if (this.viewModel.appTitle) {
      this.title.setTitle(this.textService.getText(`#(${this.viewModel.appTitle}) | #(${ShellGlobalTextKey.VENDOR_SOFTWARE})`));
    } else {
      this.title.setTitle(ShellGlobalTextKey.VENDOR_SOFTWARE);
    }
  }

  private logout() {
    this.authenticationService.logout()
      .then(() => this.router.navigate(['/login']))
      .catch((error: ApplicationError) => {
        this.alertService.alertError(error);
      });
  }

  private toggleSidebar() {
    this.componentModel.isSidebarVisible = !this.componentModel.isSidebarVisible;
    this.refreshSidebarAction(this.componentModel.toggleSidebarAction, this.componentModel.isSidebarVisible);
    this.persistSidebarState(this.componentModel.isSidebarVisible);
  }

  private persistSidebarState(value: boolean) {
    this.persistenceService.setValue(ShellPersistenceKey.IS_SIDEBAR_VISIBLE, value);
  }

  private restoreIsSidebarVisible(): boolean {
    return this.persistenceService.getValue(ShellPersistenceKey.IS_SIDEBAR_VISIBLE, false);
  }

  protected createComponentModel(): ShellComponentModel {
    // @ts-ignore
    const version = this.injector.get<string>(TimeRecordingInjectionToken.VERSION);
    const model = new ShellComponentModel({
      viewDropdown: this.createViewDropdownModel(),
      userDropdown: this.createUserDropdownModel(),
      toggleSidebarAction: this.createSidebarAction(),
      isFooterVisible: true,
      alertContainer: this.alertContainerViewService.componentModel,
      vendorName: ShellGlobalTextKey.VENDOR,
      version: version ? `v${version}` : '',
      backdropModel: this.createBackdropModel()
    });
    this.refreshComponentModel(model);
    return model;
  }

  protected refreshComponentModel(model: ShellComponentModel): void {
    // this.refreshTitle(model);
    this.refreshIsMenuVisible(model);
    this.refreshIsSidebarVisible(model);
    this.refreshSidebarAction(model.toggleSidebarAction, model.isSidebarVisible);
    this.refreshViewDropdownModel(model.viewDropdown);
    this.refreshUserDropdownModel(model.userDropdown);
  }

  private refreshIsMenuVisible(model: ShellComponentModel): void {
    const currentView = this.viewModel.view;
    model.isMenuVisible = currentView && !currentView.hideShellMenu;
  }

  private refreshIsSidebarVisible(model: ShellComponentModel): void {
    model.isSidebarVisible = this.restoreIsSidebarVisible();
  }

  private createSidebarAction(): ActionModel {
    return new ActionModel({
      onClick: () => this.toggleSidebar()
    });
  }

  private refreshSidebarAction(model: ActionModel, isSidebarVisible: boolean) {
    model.tags.set('active', isSidebarVisible);
  }

  private createViewDropdownModel(): DropdownModel {
    const model = new DropdownModel({
      tags: ['app-view-dropdown'],
      trigger: new ContainerModel({
        orientation: Orientations.SIDE_BY_SIDE,
        items: [
          new ImageModel({
            key: 'icon', tags: ['app-view-dropdown-icon'],
            type: ViewComponentKey.SVG_SYMBOL, source: ShellSvgKey.APPS
          }),
          new TextModel({
            key: 'label', tags: ['app-view-dropdown-text']
          })
        ]
      }),
    });
    this.refreshViewDropdownModel(model);
    return model;
  }

  private refreshViewDropdownModel(model: DropdownModel) {
    const currentView = this.viewModel.view;
    const labelModel = (model.trigger as ContainerModel).items.get('label') as TextModel;
    labelModel.text = currentView ? currentView.name : null;
    model.items.setValues((this.viewModel.viewList || [])
      .filter((view) => view.selectable)
      .map((view) => this.createViewDropdownItem(view)), { exclusive: true }
    );
  }

  private createViewDropdownItem(view: View): ActionModel {
    return new ActionModel({
      key: view.id, tags: { 'app-view-action': true, 'active': this.viewModel.view === view },
      type: ViewComponentKey.LINK,
      content: view.name,
      isActive: this.viewModel.view === view,
      onClick: () => this.router.navigate([`/${view.id}`])
    });
  }

  private createUserDropdownModel(): DropdownModel {
    return new DropdownModel({
      tags: ['app-user-dropdown'],
      trigger: new ImageModel({
        type: ViewComponentKey.SVG_SYMBOL,
        source: ShellSvgKey.ACCOUNT
      }),
      items: [
        new TextModel({
          key: 'userName', tags: ['app-user-name'],
          text: this.viewModel.user ? this.viewModel.user.name : null
        }),
        new ActionModel({
          key: 'logoutAction', tags: ['app-logout-action'],
          type: ViewComponentKey.LINK,
          content: new TextModel({ text: CoreActionKey.LOGOUT }),
          onClick: () => this.logout()
        }),
      ]
    });
  }

  private refreshUserDropdownModel(model: DropdownModel) {
    model.isHidden = !this.viewModel.user;
    const userNameModel = model.items.get('userName') as TextModel;
    userNameModel.text = this.viewModel.user ? this.viewModel.user.name : null;
    const logoutAction = model.items.get('logoutAction') as ActionModel;
    const logoutText = !this.viewModel.user || !/^demo-.*$/.test(this.viewModel.user.userName)
      ? CoreActionKey.LOGOUT
      : CoreActionKey.END;
    logoutAction.content.set('text', logoutText);
  }

  private createBackdropModel(): BackdropModel {
    const model = new BackdropModel({
      content: new LoadIndicatorModel()
    });
    this.refreshBackdropModel(model);
    return model;
  }

  private refreshBackdropModel(model: BackdropModel) {
    model.isHidden = !this.viewModel.isLoading;
  }

}
